/* eslint-disable max-statements */

import { Vertex2 } from '@rta/rta-blueprint-library';

import { Layer, type Vector, type VectorMetadata } from '@r-core/types';

export type convertRect2VectorsParams = {
	center: { x: number; y: number };
	height: number;
	width: number;
	layer: Layer;
	angle?: number;
	metadata?: VectorMetadata;
};

export const translateVector = <T extends Vertex2>(vector: Vector<T>): Vector<T> => {
	// const angleStart = getVectorAngle1(vector);
	const translatedVector = { ...vector };

	// short circuit
	if (vector.angle !== 0) {
		translatedVector.to.rotateAxis(vector.from, vector.angle);
	}

	return translatedVector;
};

export const convertRect2Vectors = (params: convertRect2VectorsParams): Array<Vector<Vertex2>> => {
	const { center: { x = 0, y = 0 }, height, width, layer, angle = 0, metadata = {}} = params;

	const vCommon = { metadata, layer, angle };
	const hHeight = height / 2;
	const hWidth = width / 2;

	// If angle is 0, we can use simple plain objects (no rotation needed)
	if (angle === 0) {
		// Calculate corner points directly as plain objects
		const p1 = new Vertex2({ dataIn: { x: x - hWidth, y: y + hHeight }});
		const p2 = new Vertex2({ dataIn: { x: x + hWidth, y: y + hHeight }});
		const p3 = new Vertex2({ dataIn: { x: x + hWidth, y: y - hHeight }});
		const p4 = new Vertex2({ dataIn: { x: x - hWidth, y: y - hHeight }});

		const vectors: Array<Vector<Vertex2>> = [
			{
				...vCommon,
				from: p1,
				to: p2,
				angle: 0
			},
			{
				...vCommon,
				from: p2,
				to: p3,
				angle: 90
			},
			{
				...vCommon,
				from: p3,
				to: p4,
				angle: 180
			},
			{
				...vCommon,
				from: p4,
				to: p1,
				angle: 270
			}
		];

		return vectors;
	}

	// For rotated rectangles, we need to use Vertex2 for rotation
	const center = new Vertex2({ dataIn: { x, y }});

	const p1 = translateVector({
		...vCommon,
		from: center,
		to: new Vertex2({ dataIn: { x: x - hWidth, y: y + hHeight }})
	}).to;
	const p2 = translateVector({
		...vCommon,
		from: center,
		to: new Vertex2({ dataIn: { x: x + hWidth, y: y + hHeight }})
	}).to;
	const p3 = translateVector({
		...vCommon,
		from: center,
		to: new Vertex2({ dataIn: { x: x + hWidth, y: y - hHeight }})
	}).to;
	const p4 = translateVector({
		...vCommon,
		from: center,
		to: new Vertex2({ dataIn: { x: x - hWidth, y: y - hHeight }})
	}).to;

	// Cache the coordinates immediately to avoid Vertex2 getter issues
	const p1Plain = new Vertex2({ dataIn: { x: p1.x, y: p1.y }});
	const p2Plain = new Vertex2({ dataIn: { x: p2.x, y: p2.y }});
	const p3Plain = new Vertex2({ dataIn: { x: p3.x, y: p3.y }});
	const p4Plain = new Vertex2({ dataIn: { x: p4.x, y: p4.y }});

	const vectors: Array<Vector<Vertex2>> = [
		{
			...vCommon,
			from: p1Plain,
			to: p2Plain,
			angle: 0
		},
		{
			...vCommon,
			from: p2Plain,
			to: p3Plain,
			angle: 90
		},
		{
			...vCommon,
			from: p3Plain,
			to: p4Plain,
			angle: 180
		},
		{
			...vCommon,
			from: p4Plain,
			to: p1Plain,
			angle: 270
		}
	];

	return vectors;
};
